import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Footer from "./components/Footer";
import { StoreProvider } from "./contexts/StoreContext";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import Loading from "./components/Loading";
import MainLayout from "./layouts/MainLayout";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import SolutionsPage from "./pages/SolutionsPage";
import GalleryPage from "./pages/GalleryPage";
import WhyUsPage from "./pages/WhyUsPage";
import ContactPage from "./pages/ContactPage";

// Store Components (lazy loaded)
const StoreHeader = lazy(() => import("./components/store/StoreHeader"));
const ProductListing = lazy(() => import("./pages/store/ProductListing"));
const ProductDetail = lazy(() => import("./pages/store/ProductDetail"));
const Login = lazy(() => import("./pages/store/Login"));
const Register = lazy(() => import("./pages/store/Register"));
const Cart = lazy(() => import("./pages/store/Cart"));
const Checkout = lazy(() => import("./pages/store/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/store/OrderConfirmation"));
const Orders = lazy(() => import("./pages/store/Orders"));

// Admin Components (lazy loaded)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

// Store Layout
const StoreLayout = ({ children }) => {
  return (
    <>
      <Helmet>
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Suspense fallback={<Loading />}>
        <StoreHeader />
      </Suspense>
      {children}
      <Footer />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <StoreProvider>
          <ErrorBoundary>
            <div className="App">
              <ScrollToTop />
              <Toaster position="top-right" richColors />
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={<MainLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="about" element={<AboutPage />} />
                    <Route path="services" element={<ServicesPage />} />
                    <Route path="solutions" element={<SolutionsPage />} />
                    <Route path="gallery" element={<GalleryPage />} />
                    <Route
                      path="partners"
                      element={<Navigate to="/" replace />}
                    />
                    <Route path="why-us" element={<WhyUsPage />} />
                    <Route path="contact" element={<ContactPage />} />
                  </Route>

                  {/* Store Routes */}
                  <Route
                    path="/store"
                    element={
                      <StoreLayout>
                        <ProductListing />
                      </StoreLayout>
                    }
                  />
                  <Route
                    path="/store/products"
                    element={
                      <StoreLayout>
                        <ProductListing />
                      </StoreLayout>
                    }
                  />
                  <Route
                    path="/store/product/:productId"
                    element={
                      <StoreLayout>
                        <ProductDetail />
                      </StoreLayout>
                    }
                  />
                  <Route path="/store/login" element={<Login />} />
                  <Route path="/store/register" element={<Register />} />
                  <Route
                    path="/store/cart"
                    element={
                      <StoreLayout>
                        <Cart />
                      </StoreLayout>
                    }
                  />
                  <Route
                    path="/store/checkout"
                    element={
                      <StoreLayout>
                        <Checkout />
                      </StoreLayout>
                    }
                  />
                  <Route
                    path="/store/order-confirmation"
                    element={<OrderConfirmation />}
                  />
                  <Route
                    path="/store/orders"
                    element={
                      <StoreLayout>
                        <Orders />
                      </StoreLayout>
                    }
                  />
                  <Route
                    path="/store/order/:orderId"
                    element={
                      <StoreLayout>
                        <Orders />
                      </StoreLayout>
                    }
                  />

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <>
                        <Helmet>
                          <meta name="robots" content="noindex, nofollow" />
                          <title>Admin | Nexalus Infotech</title>
                        </Helmet>
                        <AdminLogin />
                      </>
                    }
                  />
                  <Route
                    path="/admin/login"
                    element={
                      <>
                        <Helmet>
                          <meta name="robots" content="noindex, nofollow" />
                          <title>Admin Login | Nexalus Infotech</title>
                        </Helmet>
                        <AdminLogin />
                      </>
                    }
                  />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <>
                        <Helmet>
                          <meta name="robots" content="noindex, nofollow" />
                          <title>Admin Dashboard | Nexalus Infotech</title>
                        </Helmet>
                        <AdminDashboard />
                      </>
                    }
                  />

                  {/* Catch-all for store routes */}
                  <Route
                    path="/store/*"
                    element={
                      <StoreLayout>
                        <div className="min-h-screen flex items-center justify-center">
                          <div className="text-center">
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">
                              Coming Soon
                            </h1>
                            <p className="text-gray-600">
                              This page is under construction
                            </p>
                            <button
                              onClick={() =>
                                (window.location.href = "/store")
                              }
                              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                            >
                              Back to Store
                            </button>
                          </div>
                        </div>
                      </StoreLayout>
                    }
                  />
                </Routes>
              </Suspense>
            </div>
          </ErrorBoundary>
        </StoreProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
